import { useGetList, useTranslate } from "ra-core";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type MetricCardProps = {
  title: string;
  value: number | string | undefined;
  hint?: string;
  to?: string;
  tone?: "default" | "warning" | "danger" | "success";
};

const TONE_CLASSES: Record<NonNullable<MetricCardProps["tone"]>, string> = {
  default: "",
  warning:
    "border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/30",
  danger: "border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-950/30",
  success:
    "border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/30",
};

const MetricCard = ({
  title,
  value,
  hint,
  to,
  tone = "default",
}: MetricCardProps) => {
  const content = (
    <Card className={`h-full ${TONE_CLASSES[tone]}`}>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-3xl font-bold mt-2">
          {value === undefined ? "—" : value}
        </p>
        {hint && (
          <p className="text-xs text-muted-foreground mt-1">{hint}</p>
        )}
      </CardContent>
    </Card>
  );
  if (to) {
    return (
      <Link to={to} className="no-underline text-foreground">
        {content}
      </Link>
    );
  }
  return content;
};

const useCount = (
  resource: string,
  filter: Record<string, unknown> = {},
): { total: number | undefined; isPending: boolean } => {
  const { total, isPending } = useGetList(resource, {
    pagination: { page: 1, perPage: 1 },
    filter,
  });
  return { total, isPending };
};

const TopByField = ({
  resource,
  field,
  filter,
  title,
  emptyLabel,
}: {
  resource: string;
  field: string;
  filter: Record<string, unknown>;
  title: string;
  emptyLabel: string;
}) => {
  const { data, isPending } = useGetList(resource, {
    pagination: { page: 1, perPage: 200 },
    filter,
    sort: { field: "created_at", order: "DESC" },
  });
  if (isPending) return null;

  const counts = (data ?? []).reduce<Record<string, number>>((acc, row) => {
    const key =
      typeof row[field] === "string" && row[field].length
        ? (row[field] as string)
        : "(sem origem)";
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {entries.map(([key, count]) => (
              <li
                key={key}
                className="flex items-center justify-between text-sm"
              >
                <span className="truncate max-w-[60%]">{key}</span>
                <Badge variant="outline">{count}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const TopServiceInterest = () => {
  const translate = useTranslate();
  const { data: leads, isPending } = useGetList("leads", {
    pagination: { page: 1, perPage: 200 },
    filter: { is_test: false },
    sort: { field: "created_at", order: "DESC" },
  });
  const { data: services } = useGetList("service_interests", {
    pagination: { page: 1, perPage: 200 },
  });
  if (isPending) return null;

  const idToName = new Map(
    (services ?? []).map((s) => [s.id, s.name as string]),
  );
  const counts = (leads ?? []).reduce<Record<string, number>>((acc, row) => {
    const key = row.service_interest_id
      ? (idToName.get(row.service_interest_id) ?? `#${row.service_interest_id}`)
      : "(sem serviço)";
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-base">
          {translate("crm.faromarket.panel.leads_by_service", {
            _: "Leads por serviço",
          })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.faromarket.panel.empty_metric", {
              _: "Sem dados",
            })}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {entries.map(([key, count]) => (
              <li
                key={key}
                className="flex items-center justify-between text-sm"
              >
                <span className="truncate max-w-[60%]">{key}</span>
                <Badge variant="outline">{count}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export const FaroPanel = () => {
  const translate = useTranslate();

  const newLeads = useCount("leads", {
    is_test: false,
    "status@in": "(new,pending_required_data)",
  });
  const pendingSubmissions = useCount("form_submissions", {
    is_test: false,
    "status@in": "(received,pending_required_data)",
  });
  const failedSubmissions = useCount("form_submissions", {
    is_test: false,
    status: "processing_failed",
  });
  const openDeals = useCount("deals", {
    is_test: false,
    status: "open",
  });
  const pendingTasks = useCount("tasks", {
    is_test: false,
    "done_date@is": null,
  });

  return (
    <div className="flex flex-col gap-6 mt-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">
          {translate("crm.faromarket.panel.title", {
            _: "Painel operacional",
          })}
        </h2>
        <span className="text-xs text-muted-foreground">
          {translate("crm.faromarket.panel.excluding_test", {
            _: "Excluindo registros de teste",
          })}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard
          title={translate("crm.faromarket.panel.new_leads", {
            _: "Leads novos",
          })}
          value={newLeads.total}
          to="/leads"
        />
        <MetricCard
          title={translate("crm.faromarket.panel.pending_submissions", {
            _: "Submissões pendentes",
          })}
          value={pendingSubmissions.total}
          to="/form_submissions"
          tone={pendingSubmissions.total ? "warning" : "default"}
        />
        <MetricCard
          title={translate("crm.faromarket.panel.failed_submissions", {
            _: "Submissões com erro",
          })}
          value={failedSubmissions.total}
          to="/form_submissions"
          tone={failedSubmissions.total ? "danger" : "default"}
        />
        <MetricCard
          title={translate("crm.faromarket.panel.open_deals", {
            _: "Oportunidades abertas",
          })}
          value={openDeals.total}
          to="/deals"
        />
        <MetricCard
          title={translate("crm.faromarket.panel.pending_tasks", {
            _: "Tarefas pendentes",
          })}
          value={pendingTasks.total}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TopByField
          resource="leads"
          field="source"
          filter={{ is_test: false }}
          title={translate("crm.faromarket.panel.leads_by_source", {
            _: "Leads por origem",
          })}
          emptyLabel={translate("crm.faromarket.panel.empty_metric", {
            _: "Sem dados",
          })}
        />
        <TopServiceInterest />
      </div>
    </div>
  );
};
